import timeUtils from '@/utils/time.utils'
import { useRef, useState, useLayoutEffect, useCallback, useMemo, memo } from 'react'
import css from './timeLine.module.css'

interface IProps {
  tsCurrent: number
  tsFrom: number
  tsTo: number

  onSeekTo: (time: number) => void
  onMouseDown: () => void
  forceRerender: () => void
}

const PlayerWidgetTimeLine = (props: IProps) => {
  const [timeLineWidth, setTimeLineWidth] = useState<number>()

  const timeLineRef = useRef<HTMLDivElement>(null)
  const timeLineFillRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const playheadRef = useRef<HTMLDivElement>(null)
  const skimmerRef = useRef<HTMLDivElement>(null)

  const rafId = useRef<number>(null)

  const duration = useMemo(() => props.tsTo - props.tsFrom, [props.tsFrom, props.tsTo])

  const renderTimeLineMarker = (date: Date, timeLineIntervalWidth: number, index: number) => {
    return (
      <div
        key={index}
        className={css.timeLineMarkerContainer}
        style={{ width: `${timeLineIntervalWidth}%`, opacity: Number(!!index) }}
      >
        <span className="text-secondary text-sm no-select" style={{ marginBottom: '2px' }}>
          {timeUtils.formatDate(date, 'mm:ss')}
        </span>
      </div>
    )
  }

  const timeLineMarkersData = useMemo(() => {
    if (!props.tsTo || !props.tsFrom || !timeLineWidth) return null
    const timeLineMarkerTypes = [1, 5, 10, 30, 60, 90, 120]
    const minMarkerPx = Math.min(timeLineWidth, 40)
    const minTimeLineIntervalWidth = Math.min(100, Math.ceil((minMarkerPx / timeLineWidth) * 100))

    let bestIntervals = []
    let bestMarkerWidth = 100

    for (let type of timeLineMarkerTypes) {
      const tempIntervalByType = timeUtils.eachSecondsOfInterval(
        { start: props.tsFrom, end: props.tsTo - 1 },
        { step: type }
      )
      if (tempIntervalByType.length === 0) continue

      const tempTimeLineIntervalWidth = Math.floor(100 / tempIntervalByType.length)

      if (tempTimeLineIntervalWidth < minTimeLineIntervalWidth) continue

      bestIntervals = [...tempIntervalByType]
      bestMarkerWidth = tempTimeLineIntervalWidth
      break
    }

    return {
      intervals: bestIntervals.length ? bestIntervals : [props.tsFrom],
      markerWidth: bestMarkerWidth,
    }
  }, [props.tsTo, props.tsFrom, timeLineWidth])

  const renderTimeLineMarkers = () => {
    if (!timeLineMarkersData) return null

    return timeLineMarkersData.intervals.map((interval, index) =>
      renderTimeLineMarker(interval, timeLineMarkersData.markerWidth, index)
    )
  }

  const onTimeLineEnter = useCallback(() => {
    if (!tooltipRef.current || !skimmerRef.current || !timeLineRef.current) return

    const calcXCursorInTimeLine = (xMousePos: number) => {
      const rect = timeLineRef.current.getBoundingClientRect()
      const x = xMousePos - rect.left
      return Math.max(0, Math.min(x, rect.width))
    }

    const movePlayhead = (xMousePos: number) => {
      const clampedX = calcXCursorInTimeLine(xMousePos)

      playheadRef.current.style.transform = `translateX(${clampedX}px)`
      playheadRef.current.dataset.active = 'true'
      timeLineFillRef.current.style.width = `${clampedX}px`
      skimmerRef.current.style.opacity = '0'
    }

    const onMouseMove = (e: MouseEvent) => {
      if (rafId.current) return

      const clampedX = calcXCursorInTimeLine(e.clientX)
      const progress = clampedX / timeLineWidth

      const time = duration * progress + props.tsFrom

      rafId.current = requestAnimationFrame(() => {
        tooltipRef.current.style.opacity = '1'
        tooltipRef.current.textContent = timeUtils.formatDate(time, 'dd MMM, HH:mm:ss')
        tooltipRef.current.style.transform = `translateX(${clampedX}px) translateX(-50%)`

        skimmerRef.current.style.transform = `translateX(${clampedX}px)`
        skimmerRef.current.style.opacity = '1'
        if ((e.buttons & 1) !== 0) {
          movePlayhead(e.clientX)
        }
        rafId.current = null
      })
    }

    const onMouseDown = (e: MouseEvent) => {
      if ((e.buttons & 1) !== 0) {
        props.onMouseDown()
        movePlayhead(e.clientX)
      }
    }

    const onMouseUp = (e: MouseEvent) => {
      const clampedX = calcXCursorInTimeLine(e.clientX)
      const progress = clampedX / timeLineWidth
      const time = duration * progress + props.tsFrom

      props.onSeekTo(time)
      skimmerRef.current.style.opacity = '1'
      playheadRef.current.dataset.active = 'false'
    }

    const handleLeave = () => {
      rafId.current = requestAnimationFrame(() => {
        tooltipRef.current.style.opacity = '0'
        skimmerRef.current.style.opacity = '0'
        playheadRef.current.dataset.active = 'false'
        rafId.current = null
      })

      timeLineRef.current.removeEventListener('mousemove', onMouseMove)
      timeLineRef.current.removeEventListener('mouseleave', handleLeave)

      window.removeEventListener('mousedown', onMouseDown)
      window.removeEventListener('mouseup', onMouseUp)
    }

    timeLineRef.current.addEventListener('mousemove', onMouseMove)
    timeLineRef.current.addEventListener('mouseleave', handleLeave)

    window.addEventListener('mousedown', onMouseDown)
    window.addEventListener('mouseup', onMouseUp)
  }, [duration, props.tsFrom, timeLineWidth, props.onSeekTo])

  useLayoutEffect(() => {
    if (!timeLineRef.current) return

    const observer = new ResizeObserver(() => {
      setTimeLineWidth(timeLineRef.current.getBoundingClientRect().width)
    })

    observer.observe(timeLineRef.current)

    timeLineRef.current.addEventListener('mouseenter', onTimeLineEnter)

    return () => {
      if (!timeLineRef.current) return
      observer.disconnect()
      timeLineRef.current.removeEventListener('mouseenter', onTimeLineEnter)
    }
  }, [onTimeLineEnter])

  const renderTooltip = () => {
    return (
      <div
        ref={tooltipRef}
        className="no-select"
        style={{
          position: 'absolute',
          bottom: '-30px',
          left: 0,
          transform: 'translateX(-50%)',
          background: '#000',
          color: '#fff',
          fontSize: 12,
          padding: '2px 6px',
          borderRadius: 4,
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          opacity: 0,
          transition: 'opacity 120ms',
        }}
      />
    )
  }

  return (
    <div // TODO сделать управление стрелками
      ref={timeLineRef}
      className={css.timeLine}
    >
      <div className={css.horLine} />
      <div
        className={css.timeLineFill}
        ref={timeLineFillRef}
        style={{
          width: `${((props.tsCurrent - props.tsFrom) / duration) * timeLineWidth}px`,
        }}
      />
      {renderTimeLineMarkers()}
      {renderTooltip()}
      <div
        ref={playheadRef}
        data-active={false}
        style={{
          transform: `translateX(${((props.tsCurrent - props.tsFrom) / duration) * timeLineWidth}px)`,
        }}
        className={css.playhead}
      />
      <div ref={skimmerRef} className={css.skimmer} />
    </div>
  )
}

export default memo(PlayerWidgetTimeLine)
